import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../../components/button/button'
import { NativeInputBox } from '../../components/nativeInputBox/nativeInputBox'
import { QwertyKeyboard } from '../../components/QwertyKeyboard/QwertyKeyboard'
import SpeechComp from '../../components/speechComp/speechComp'
import { getColor, grey300, grey50, grey960, white } from '../../constants/colors'
import { UniNeueHeavy, UniNeueRegular } from '../../constants/fonts'
import { LOGIN_ICON_HEIGHT, LOGIN_ICON_WIDTH } from '../../constants/general'
import { Images } from '../../constants/paths'
import { FORGOT_PASSWORD, HOME } from '../../constants/routes'
import { AppConfig } from '../../services/appConfig'
import { Flo } from '../../types/flo-auth-api'
import { storeUserData, updateUserSubscriptionData } from '../../utils/authentication'
import { getText } from '../../utils/dictionary'
import { getAsset, getIcon } from '../../utils/image'
import { validateEmail } from '../../utils/misc'
import { captureSessionData } from '../../utils/segmentAnalytics'

export enum LoginStates {
  FocusOnParent = 'FocusOnParent',
  EnterEmail = 'EnterEmail',
  EnterPassword = 'EnterPassword',
  Keyboard = 'Keyboard',
}

export default class LoginRemoteControlHandler extends SpeechComp implements ITab {
  private _currentButton: Lightning.Component
  private _lastState: string
  private _email: string
  private _password: string
  private _continueBtnEnabled: boolean
  private _goBack: boolean
  private _goSteps: number

  static _template() {
    return {
      w: 1920,
      h: 888,
      rect: true,
      texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGIN_WITH_REMOTE_BG), 1920, 888),
      Container: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'column',
          alignItems: 'center',
        },
        EmailContainer: {
          flex: {
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          },
          Title: {
            flexItem: {
              marginTop: 48,
            },
            w: 668,
            text: {
              textAlign: 'center',
              text: getText('enterYourEmailAddress'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 56,
              letterSpacing: 0.48,
              fontFace: UniNeueHeavy,
            },
          },
          EmailInputField: {
            flexItem: {
              marginTop: 32,
            },
            type: NativeInputBox,
            w: 668,
            h: 84,
            placeholder: 'Email',
            fontSize: 32,
            textColor: getColor(grey50),
            placeholderColor: getColor(grey300),
            color: getColor(grey960),
            fontFace: UniNeueRegular,
            iconImage: Lightning.Tools.getSvgTexture(
              getIcon('icon-envelope'),
              LOGIN_ICON_WIDTH,
              LOGIN_ICON_HEIGHT,
            ),
            iconColor: getColor(grey50),
            onCompleteSignal: 'onEmailComplete',
            onTextChangeSignal: 'onEmailChange',
            signals: {
              onEmailComplete: 'onEmailComplete',
              onEmailChange: 'onEmailChange',
            },
          },
          ContinueButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            type: Button,
            label: getText('continue'),
            isEnabled: false,
          },
        },
        PasswordContainer: {
          visible: false,
          flex: {
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          },
          Title: {
            flexItem: {
              marginTop: 48,
            },
            w: 668,
            text: {
              textAlign: 'center',
              text: getText('enterYourPassword'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 56,
              letterSpacing: 0.48,
              fontFace: UniNeueHeavy,
            },
          },
          PasswordInputField: {
            flexItem: {
              marginTop: 32,
            },
            type: NativeInputBox,
            w: 668,
            h: 84,
            placeholder: 'Password',
            fontSize: 32,
            textColor: getColor(grey50),
            placeholderColor: getColor(grey300),
            color: getColor(grey960),
            fontFace: UniNeueRegular,
            isPassword: true,
            iconImage: Lightning.Tools.getSvgTexture(
              getIcon('icon-lock'),
              LOGIN_ICON_WIDTH,
              LOGIN_ICON_HEIGHT,
            ),
            iconColor: getColor(grey50),
            onCompleteSignal: 'onPasswordComplete',
            signals: {
              onPasswordComplete: 'onPasswordComplete',
            },
          },
          LogInButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            type: Button,
            label: getText('loginRemoteLoginButton'),
          },
          TogglePasswordButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            type: Button,
            label: getText('showPassword'),
          },
          ForgotPasswordButton: {
            flexItem: {
              marginTop: 32,
            },
            w: 668,
            type: Button,
            label: getText('loginRemoteForgotPasswordButton'),
            isTransparent: true,
          },
        },
      },
      Keyboard: {
        x: 540,
        y: 425,
        visible: isVizio,
        type: QwertyKeyboard,
      },
    }
  }

  get pageText() {
    return 'Login With Remote Control'
  }

  get sectionText() {
    return 'Scroll Left to Login with Smartphone'
  }

  get speechText() {
    return 'Scroll Down to Enter Your Email'
  }

  get keyboard() {
    return this.tag('Keyboard')
  }

  get emailInputField() {
    return this.tag('EmailInputField') as NativeInputBox
  }

  get continueButton() {
    return this.tag('ContinueButton') as Button
  }

  get passwordInputField() {
    return this.tag('PasswordInputField') as NativeInputBox
  }

  get loginButton() {
    return this.tag('LogInButton') as Lightning.Component
  }

  get togglePasswordButton() {
    return this.tag('TogglePasswordButton') as Button
  }

  get forgotPasswordButton() {
    return this.tag('ForgotPasswordButton') as Lightning.Component
  }

  get emailContainer() {
    return this.tag('EmailContainer') as Lightning.Component
  }

  get passwordContainer() {
    return this.tag('PasswordContainer') as Lightning.Component
  }

  get email() {
    return this._email
  }

  set email(v: string) {
    this._email = v
    this.emailInputField.setText(v)
    this.onEmailChange(v)
  }

  set goBack(v: boolean) {
    this._goBack = v
  }

  set goSteps(v: number) {
    this._goSteps = v
  }

  get lastState() {
    return this._lastState
  }

  _init() {
    this._currentButton = this.emailInputField
    this._lastState = LoginStates.EnterEmail
    this._setState(LoginStates.FocusOnParent)
  }

  _active() {
    if (!this._email) {
      this._currentButton = this.emailInputField
      this._lastState = LoginStates.EnterEmail
    } else {
      this._currentButton = this.passwordInputField
      this._lastState = LoginStates.EnterPassword
      this.showPasswordContainer()
    }
    this._setState(LoginStates.FocusOnParent)
    this.speak()
  }

  _inactive() {
    this.emailInputField?.closeInput()
    this.passwordInputField.closeInput()
  }

  static _states() {
    return [
      class Keyboard extends this {
        _getFocused() {
          return this.keyboard
        }
      },

      class FocusOnParent extends this {
        _getFocused() {
          return null
        }

        _handleDown() {
          this._setState(this._lastState)
        }
      },

      class EnterEmail extends this {
        get sectionText() {
          return getText('enterYourEmailAddress')
        }

        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this._lastState = this._getState()
          this.showEmailContainer()
          this._currentButton = this.emailInputField
        }

        _handleEnter() {
          if (this._currentButton === this.emailInputField) {
            this.emailInputField.startInput()
          } else if (this._currentButton === this.continueButton) {
            this._email = this.emailInputField.getText()
            this._setState(LoginStates.EnterPassword)
          }
        }

        _handleDown() {
          if (this._currentButton === this.emailInputField && this._continueBtnEnabled) {
            this._currentButton = this.continueButton
          }
        }

        _handleUp() {
          if (this._currentButton === this.continueButton) {
            this._currentButton = this.emailInputField

            if (!isVizio) {
              this.emailInputField.startInput()
            }
          } else if (this._currentButton === this.emailInputField) {
            this.fireAncestors('$returnFocusToParent')
            this._setState(LoginStates.FocusOnParent)
          }
        }
      },
      class EnterPassword extends this {
        get sectionText() {
          return getText('enterYourPassword')
        }

        _getFocused() {
          return this._currentButton
        }

        $enter() {
          this._lastState = this._getState()
          this.showPasswordContainer()
          this._currentButton = this.passwordInputField
        }

        _handleEnter() {
          if (this._currentButton === this.passwordInputField) {
            this.passwordInputField.startInput()
          } else if (this._currentButton === this.loginButton) {
            this._password = this.passwordInputField.getText()
            this.doLogin()
          } else if (this._currentButton === this.togglePasswordButton) {
            this.passwordInputField.toggleIsPassword()
            this.togglePasswordButton.label = getText(
              this.passwordInputField.isPassword ? 'showPassword' : 'hidePassword',
            )
          } else if (this._currentButton === this.forgotPasswordButton) {
            Router.navigate(FORGOT_PASSWORD, { email: this._email })
          }
        }

        _handleBack() {
          this._setState(LoginStates.EnterEmail)
        }

        _handleDown() {
          if (this._currentButton === this.passwordInputField) {
            this._currentButton = this.loginButton
          } else if (this._currentButton === this.loginButton) {
            this._currentButton = this.togglePasswordButton
          } else if (this._currentButton === this.togglePasswordButton) {
            this._currentButton = this.forgotPasswordButton
          }
        }

        _handleUp() {
          if (this._currentButton === this.forgotPasswordButton) {
            this._currentButton = this.togglePasswordButton
          } else if (this._currentButton === this.togglePasswordButton) {
            this._currentButton = this.loginButton
          } else if (this._currentButton === this.loginButton) {
            this._currentButton = this.passwordInputField
          } else if (this._currentButton === this.passwordInputField) {
            this.fireAncestors('$returnFocusToParent')
            this._setState(LoginStates.FocusOnParent)
          }
        }
      },
    ]
  }

  getFirstFocusElement(): Lightning.Component {
    return this._currentButton ?? this.emailInputField
  }

  getFocusedElement(): Lightning.Component {
    return this._currentButton
  }

  focusFirstElement(): void {
    this._currentButton = this._currentButton ?? this.emailInputField
    this._setState(this._lastState)
  }

  isAtTopMostElement(): boolean {
    return false
  }

  showEmailContainer() {
    this.passwordContainer.visible = false
    this.emailContainer.visible = true
  }

  showPasswordContainer() {
    this.emailContainer.visible = false
    this.passwordContainer.visible = true
  }

  onEmailChange(value) {
    this._continueBtnEnabled = validateEmail(value)
    this.continueButton.isEnabled = this._continueBtnEnabled
  }

  onEmailComplete() {
    // For mouse mode, reapplies focused styles, as the input is already focused, so calling this._refocus() will not trigger them
    this.emailInputField._focus()
  }

  onPasswordComplete() {
    // For mouse mode, reapplies focused styles, as the input is already focused, so calling this._refocus() will not trigger them
    this.passwordInputField._focus()
  }

  async doLogin() {
    this.fireAncestors('$toggleLoading', true)

    const data = await AppConfig.shared.authService.login({
      email: this._email,
      password: this._password,
    })

    if ('error' in data && data.isNetworkError) {
      this.fireAncestors('$toggleLoading', false)
      this.fireAncestors('$connectionError', {
        title: getText('nonBffConnectionErrorTitle'),
        info: getText('connectionErrorSubtitle'),
        footer: getText('loginRemoteErrorFooter'),
        retryAction: () => {
          this._setState(this._lastState)
          Router.focusPage()
        },
      })
      return
    }

    const authToken = (data as Flo.Auth.LoginResponse)?.token
    const refreshToken = (data as Flo.Auth.LoginResponse)?.refresh_token

    if (!authToken || !refreshToken) {
      this.fireAncestors('$toggleLoading', false)
      this.fireAncestors('$errorUserEmailPassword', {
        info: getText('loginRemoteError'),
        footer: getText('loginRemoteErrorFooter'),
        retryAction: () => {
          this._setState(this._lastState)
          Router.focusPage()
        },
      })
      return
    }

    storeUserData(data as Flo.Auth.LoginResponse)

    // Update user subscription data if needed
    updateUserSubscriptionData()

    AppConfig.shared.appSettings.homeData = null

    if (isTizen) {
      await AppConfig.shared.samsungCWService.restoreContinueWatching()
    }

    void captureSessionData()

    if (this._goBack) {
      Router.go(this._goSteps)
    } else {
      Router.navigate(HOME)
    }
  }

  $updateFocusByHover(element) {
    if (this.emailInputField.inputCreated || this.passwordInputField.inputCreated) {
      // Prevents the keyboard from closing if active and any of the buttons gets focused or if the BACKSPACE key from
      // the virtual keyboard of LG is pressed
      return
    }

    this._currentButton = element
    this._refocus()
  }

  _handleClick(element) {
    if (element === this.continueButton) {
      this.emailInputField.closeInput()
    } else if (
      element === this.loginButton ||
      element === this.togglePasswordButton ||
      element === this.forgotPasswordButton
    ) {
      this.passwordInputField.closeInput()
    }

    if (element === this.emailInputField || element === this.continueButton) {
      this._setState(LoginStates.EnterEmail)
      this._currentButton = element

      if (this._currentButton === this.emailInputField) {
        this.emailInputField.resetCursor()
      }

      ;(this as any)._handleEnter()
    } else if (
      element === this.passwordInputField ||
      element === this.loginButton ||
      element === this.togglePasswordButton ||
      element === this.forgotPasswordButton
    ) {
      this._setState(LoginStates.EnterPassword)
      this._currentButton = element

      if (this._currentButton === this.passwordInputField) {
        this.passwordInputField.resetCursor()
      }

      ;(this as any)._handleEnter()
    }

    // Need to bubble up this event so that we change the state of the parent component on its click handler
    return false
  }

  validateEmailString() {
    const isEmailView = this.emailContainer.visible
    const input = this.emailContainer.visible ? this.emailInputField : this.passwordInputField
    let curText = input.getText()

    if (isEmailView) {
      this._continueBtnEnabled = validateEmail(curText)
      this.continueButton.isEnabled = this._continueBtnEnabled
    }
  }

  $focusVizioKeyboard() {
    this._lastState = this.emailContainer.visible
      ? LoginStates.EnterEmail
      : LoginStates.EnterPassword
    this._setState(LoginStates.Keyboard)
  }

  $unfocusVizioKeyboard() {
    this.validateEmailString()
    this._setState(this._lastState)
  }

  $onVizioKeyboardKeyPress(key: string) {
    const isEmailView = this.emailContainer.visible
    const input = isEmailView ? this.emailInputField : this.passwordInputField
    let curText = input.getText()

    switch (key) {
      case 'Done':
        this.validateEmailString()
        this._setState(this._lastState)
        return

      case 'Clear':
        input.setText('')
        return

      case 'Backspace':
        if (curText.length > 0) {
          input.setText(curText.slice(0, -1))
        } else {
          this.$unfocusVizioKeyboard()
        }
        return

      case 'Space':
        input.setText(curText + ' ')
        return

      default:
        input.setText(curText === '' ? key : curText + key)
    }
  }
}
